<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] rounded-md flex flex-col h-full min-h-[500px] shadow-lg overflow-hidden">
    <!-- Title Card - Full width with no side margins -->
    <div class="w-full bg-primary-500 shadow-md p-4 mb-5">
      <div class="max-w-[200px] mx-auto">
        <h3 class="text-lg font-medium text-center text-white">Property Notes</h3>
      </div>
    </div>
    
    <!-- Content area with padding -->
    <div class="flex-grow flex flex-col px-[20px] md:px-[25px] pb-[20px] md:pb-[25px]">
      <!-- Loading State -->
      <div v-if="loading" class="flex-grow flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 opacity-75"></div>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="flex-grow flex flex-col justify-center items-center py-5">
        <div class="text-danger-500 text-xl mb-2">
          <i class="ri-error-warning-line"></i>
        </div>
        <p class="text-danger-500">{{ error }}</p>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="notes.length === 0" class="flex-grow flex flex-col justify-center items-center py-5">
        <p class="text-gray-500 dark:text-gray-400 mb-3">No notes have been added yet.</p>
      </div>
      
      <!-- Notes List -->
      <div v-else class="flex-grow overflow-y-auto mb-4 pr-1">
        <div 
          v-for="note in notes" 
          :key="note.id" 
          class="mb-4 p-3 bg-white dark:bg-[#172036] border border-gray-100 dark:border-[#172036] rounded-2xl shadow-md"
        >
          <div class="flex items-start justify-between mb-2">
            <span class="font-medium text-sm text-gray-700 dark:text-gray-300">
              {{ getDisplayName(note.userid) }}
            </span>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ formatDate(note.created_at) }}
            </span>
          </div>
          <p class="text-gray-700 dark:text-gray-300 whitespace-pre-line">{{ note.note }}</p>
        </div>
      </div>
      
      <!-- Add Note Form - Fixed at the bottom -->
      <div class="mt-auto">
        <textarea
          v-model="newNote"
          rows="3"
          class="w-full px-3 py-2 border border-gray-200 dark:border-[#172036] rounded-md bg-white dark:bg-[#0c1427] text-gray-700 dark:text-gray-300 focus:border-primary-500 focus:outline-none resize-none"
          placeholder="Add a note..."
        ></textarea>
        
        <div class="flex justify-end mt-3">
          <button
            @click="addNote"
            :disabled="isSubmitting || !newNote.trim()"
            class="px-4 py-2 bg-primary-500 text-white rounded-md font-medium hover:bg-primary-400 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-500"
          >
            {{ isSubmitting ? 'Adding...' : 'Add Note' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { supabase } from '@/utils/supabase';

interface PropertyNote {
  id: string;
  userid: string;
  property_id: string;
  note: string;
  created_at: string;
  user?: {
    display_name: string;
  };
}

interface User {
  id: string;
  display_name: string;
}

export default defineComponent({
  name: 'PropertyNotes',
  
  setup() {
    const route = useRoute();
    const notes = ref<PropertyNote[]>([]);
    const users = ref<Record<string, User>>({});
    const loading = ref(true);
    const error = ref('');
    const newNote = ref('');
    const isSubmitting = ref(false);
    const isComponentMounted = ref(true);
    
    const loadNotes = async () => {
      // Skip if component is unmounted
      if (!isComponentMounted.value) return;
      
      try {
        loading.value = true;
        error.value = '';
        
        const propertyId = route.query.id as string;
        if (!propertyId) {
          loading.value = false;
          return;
        }
        
        // Fetch notes for this property
        const { data, error: fetchError } = await supabase
          .from('propertynotes')
          .select('*')
          .eq('property_id', propertyId)
          .order('created_at', { ascending: false });
          
        // Skip if component is unmounted after fetch
        if (!isComponentMounted.value) return;
          
        if (fetchError) {
          throw new Error(fetchError.message);
        }
        
        notes.value = data || [];
        
        // Get a list of unique user IDs from the notes
        const userIds = [...new Set(notes.value.map(note => note.userid))];
        if (userIds.length > 0 && isComponentMounted.value) {
          await loadUserDetails(userIds);
        }
        
      } catch (err) {
        if (!isComponentMounted.value) return;
        
        console.error('Error loading notes:', err);
        error.value = err instanceof Error ? err.message : 'Failed to load property notes';
      } finally {
        if (isComponentMounted.value) {
          loading.value = false;
        }
      }
    };
    
    const loadUserDetails = async (userIds: string[]) => {
      if (!isComponentMounted.value) return;
      
      try {
        // Fetch user profiles for these IDs
        const { data, error: userError } = await supabase
          .from('user_profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);
          
        if (!isComponentMounted.value) return;
          
        if (userError) {
          console.error('Error fetching user details:', userError);
          return;
        }
        
        // Create a map of user IDs to display names
        if (data) {
          const userMap = { ...users.value };
          data.forEach(user => {
            userMap[user.user_id] = {
              id: user.user_id,
              display_name: user.display_name || 'Unknown User'
            };
          });
          users.value = userMap;
        }
      } catch (err) {
        if (!isComponentMounted.value) return;
        console.error('Error in loadUserDetails:', err);
      }
    };
    
    const getDisplayName = (userId: string) => {
      return users.value[userId]?.display_name || 'Unknown User';
    };
    
    const formatDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      } catch (err) {
        console.error('Error formatting date:', err);
        return dateString;
      }
    };
    
    const getCurrentUser = async () => {
      if (!isComponentMounted.value) return null;
      
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) {
          return data.user;
        }
      } catch (err) {
        console.error('Error getting current user:', err);
      }
      return null;
    };
    
    const addNote = async () => {
      if (!newNote.value.trim() || !isComponentMounted.value) return;
      
      try {
        isSubmitting.value = true;
        
        const user = await getCurrentUser();
        if (!user) {
          if (isComponentMounted.value) {
            error.value = 'You must be logged in to add notes';
          }
          return;
        }
        
        const propertyId = route.query.id as string;
        if (!propertyId) {
          if (isComponentMounted.value) {
            error.value = 'Property ID is required';
          }
          return;
        }
        
        // Skip if component was unmounted during async operations
        if (!isComponentMounted.value) return;
        
        // Add the new note
        const { error: insertError } = await supabase
          .from('propertynotes')
          .insert({
            userid: user.id,
            property_id: propertyId,
            note: newNote.value.trim()
          });
          
        // Skip if component was unmounted
        if (!isComponentMounted.value) return;
          
        if (insertError) {
          throw new Error(insertError.message);
        }
        
        // Reset form and reload notes
        newNote.value = '';
        await loadNotes();
        
      } catch (err) {
        if (!isComponentMounted.value) return;
        
        console.error('Error adding note:', err);
        error.value = err instanceof Error ? err.message : 'Failed to add note';
      } finally {
        if (isComponentMounted.value) {
          isSubmitting.value = false;
        }
      }
    };
    
    // Watch for changes in route query to reload data
    const stopWatcher = watch(() => route.query.id, (newId) => {
      if (newId && isComponentMounted.value) {
        loadNotes();
      }
    });
    
    // Load notes when the component mounts
    onMounted(() => {
      isComponentMounted.value = true;
      loadNotes();
    });
    
    // Cleanup when component unmounts
    onUnmounted(() => {
      isComponentMounted.value = false;
      stopWatcher(); // Stop the watcher
    });
    
    return {
      notes,
      loading,
      error,
      newNote,
      isSubmitting,
      getDisplayName,
      formatDate,
      addNote
    };
  }
});
</script>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}
</style> 